import React, { useEffect } from 'react';
import { connect } from 'react-redux';

import * as Sentry from '@sentry/react';
import amplitude from 'amplitude-js';
import { toggleSpinner } from 'apps/auth/actions/appAction';
import { toggleChat } from 'apps/auth/actions/chatAction';
import { InfoPage } from 'apps/auth/components/common/Layout/InfoPage/InfoPage';
import { amplitudeLogout } from 'apps/auth/components/utils/AmplitudeUtils';
import ModalComponent from 'apps/auth/components/utils/ModalComponent';
import { ADVANCED_ERROR_SCREEN_SHOWN, GET_CHAT_CLICKED, LOGOUT_COMPLETED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { ERROR, FROM } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { FRAUD_HARD_BLOCK } from 'apps/auth/constants/amplitude/AmplitudeParamValues';
import { INVALID_DATA, REG_NOT_FINISHED, SESSION_EXPIRED, TOS_NOT_COMPLIED } from 'apps/auth/constants/Constants';
import { LOGIN } from 'apps/auth/constants/UrlConstants';
import { logWarn } from 'apps/auth/helpers/clientLogger';
import { hardBlockErrorType } from 'apps/auth/helpers/utils';
import { useAuth, useExtra, usePageMessages } from 'apps/auth/hooks/selectors';

const FraudHardBlockInfoModal = (props) => {
    const { ballId, countryId } = useAuth();
    const { amplitude_screen_activity } = useExtra();
    const {
        tosNotCompliedTitle,
        tosNotCompliedText,
        regNotFinishedTitle,
        regNotFinishedText,
        sessionExpiredTitle,
        sessionExpiredText,
        invalidDataTitle,
        invalidDataText,
        gotIt,
        getHelp,
        login,
    } = usePageMessages();

    const error = hardBlockErrorType(props.error?.errorCode);

    useEffect(() => {
        amplitude.getInstance().logEvent(ADVANCED_ERROR_SCREEN_SHOWN, { [ERROR]: props.error?.errorCode });
        Sentry.captureMessage('Fraud block error', {
            extra: {
                ball_id: ballId || '',
                countryId: countryId,
                errorCode: props.error.errorCode,
                errorMessage: props.error.errorMessage,
                screenActivity: amplitude_screen_activity || '',
            },
            level: 'info',
        });
    }, [amplitude_screen_activity]);

    const logout = (e) => {
        e?.preventDefault();
        props.toggleSpinner(true);
        fetch('/logout', { method: 'GET' })
            .then((response) => {
                if (response.ok) {
                    window.location.replace(LOGIN);
                    amplitude.getInstance().logEvent(LOGOUT_COMPLETED, { [FROM]: FRAUD_HARD_BLOCK });
                    amplitudeLogout();
                }
            })
            .catch((error) => {
                logWarn(error);
                props.toggleSpinner(false);
                return null;
            });
    };

    const closeModal = (e) => {
        if (error === REG_NOT_FINISHED || error === INVALID_DATA) {
            logout(e);
        }
    };

    const getTitle = () => {
        let title;
        if (error === TOS_NOT_COMPLIED) {
            title = tosNotCompliedTitle;
        } else if (error === REG_NOT_FINISHED) {
            title = regNotFinishedTitle;
        } else if (error === SESSION_EXPIRED) {
            title = sessionExpiredTitle;
        } else if (error === INVALID_DATA) {
            title = invalidDataTitle;
        }
        return title;
    };

    const getText = () => {
        let text;
        if (error === TOS_NOT_COMPLIED) {
            text = tosNotCompliedText;
        } else if (error === REG_NOT_FINISHED) {
            text = regNotFinishedText;
        } else if (error === SESSION_EXPIRED) {
            text = sessionExpiredText;
        } else if (error === INVALID_DATA) {
            text = invalidDataText;
        }
        return text;
    };

    const getButtonCaption = () => {
        let caption;
        if (error === TOS_NOT_COMPLIED) {
            caption = gotIt;
        } else if (error === REG_NOT_FINISHED || error === INVALID_DATA) {
            caption = getHelp;
        } else if (error === SESSION_EXPIRED) {
            caption = login;
        }
        return caption;
    };

    const onButtonClick = (e) => {
        if (error === REG_NOT_FINISHED || error === INVALID_DATA) {
            amplitude.getInstance().logEvent(GET_CHAT_CLICKED);
            props.toggleChat(true);
        } else {
            logout(e);
        }
    };

    return (
        <>
            <ModalComponent
                isVerticalCentered
                canCloseModal={error === REG_NOT_FINISHED || error === INVALID_DATA}
                isIconClose={error === REG_NOT_FINISHED || error === INVALID_DATA}
                modalRef={props.modalRef}
                onCloseIconClick={closeModal}
            >
                <InfoPage
                    insideModalComponent
                    iconName="alert-circle-80"
                    iconColor="primary"
                    headerText={getTitle()}
                    secondaryText={<div dangerouslySetInnerHTML={{ __html: getText() }} />}
                    primaryButtonCaption={getButtonCaption()}
                    primaryButtonType="button"
                    primaryButtonOnClick={onButtonClick}
                    isTagA
                />
            </ModalComponent>
        </>
    );
};

export default connect(null, { toggleChat, toggleSpinner })(FraudHardBlockInfoModal);
